/** Port von `Client/Pages/Views/IwmViewRechnungen.razor`. */

import { useState } from 'react';

import { receiptApi } from '@/api/services/receiptApi';
import { receiptEditTemplates } from '@/components/columns/edit/receiptEditColumns';
import { receiptCellTemplates } from '@/components/columns/receiptColumns';
import { ReceiptAssignmentDialog } from '@/components/dialogs/ReceiptAssignmentDialog';
import { ReceiptDocumentListDialog } from '@/components/dialogs/ReceiptDocumentListDialog';
import { ReceiptDocumentUploadDialog } from '@/components/dialogs/ReceiptDocumentUploadDialog';
import { DataGrid } from '@/components/grid/DataGrid';
import { ReceiptSidebar } from '@/components/sidebars/ReceiptSidebar';
import { useSidebar } from '@/components/sidebars/useSidebar';
import { Button } from '@/components/ui';
import { AuditButton } from '@/components/views/AuditButton';
import { DeleteButton } from '@/components/views/DeleteButton';
import { DocumentsDropDown } from '@/components/views/DocumentsDropDown';
import { ExcelExportButton } from '@/components/views/ExcelExportButton';
import { MessageDialog } from '@/components/views/MessageDialog';
import { NewButton } from '@/components/views/NewButton';
import { ViewAuditSidebar } from '@/components/views/ViewAuditSidebar';
import { ViewPageFrame } from '@/components/views/ViewPageFrame';
import { firstSelected } from '@/components/views/firstSelected';
import { useMessageDialog } from '@/components/views/useMessageDialog';
import { useViewIdParam } from '@/components/views/useViewIdParam';
import { useViewPage } from '@/hooks/useViewPage';
import type { Receipt } from '@/types/entities';
import { EntityTypes } from '@/types/enums';

export function ReceiptView() {
  const viewId = useViewIdParam();
  const page = useViewPage<Receipt>({
    viewId,
    entity: EntityTypes.Rechnung,
    api: receiptApi,
    cellTemplates: receiptCellTemplates,
    editTemplates: receiptEditTemplates,
  });
  const { permissions, selectedId, view } = page;
  const message = useMessageDialog();
  const refreshGrid = (): void => void page.gridRef.current?.refresh();

  // `RechnungId` wird im Altclient nur gesetzt, nie zurückgenommen.
  const [rechnungId, setRechnungId] = useState(0);
  const auditSidebar = useSidebar();
  const receiptSidebar = useSidebar({ onHide: refreshGrid });
  // `IwmBaseDialog` und `IwmBaseSidebar` teilen den Vertrag `Visible`/`Show()`/`Hide()`;
  // die Sichtbarkeit der Dialoge hängt deshalb am selben Regler.
  const uploadDialog = useSidebar();
  const listDialog = useSidebar();

  // Port von `ReceiptAssignment()`.
  const receiptAssignment = (): void => {
    message.show('Assign to Container', 'Assign to Container');
    const records = page.gridRef.current?.getSelectedRecords() ?? [];
    if (records.length !== 1) {
      message.setText('Please choose a dataset.');
      return;
    }
    setRechnungId(firstSelected(records).Id);
  };

  const toolbar = (
    <>
      {permissions?.AllowCreate === true ? (
        <NewButton
          onClick={() => {
            receiptSidebar.show();
          }}
        />
      ) : null}
      {permissions?.AllowAudit === true ? (
        <AuditButton
          selectedId={selectedId}
          onClick={() => {
            auditSidebar.show();
          }}
        />
      ) : null}
      {view?.AdditionalFunctions?.includes('Dokument') === true ? (
        <DocumentsDropDown
          selectedId={selectedId}
          onUpload={() => {
            uploadDialog.show();
          }}
          onList={() => {
            listDialog.show();
          }}
        />
      ) : null}
      {/* Der Schlüssel ist im Altclient englisch, obwohl die übrigen deutsch sind. */}
      {view?.AdditionalFunctions?.includes('ReceiptAssignment') === true ? (
        <Button className="button-c" disabled={selectedId === null} onClick={receiptAssignment}>
          Assign to Container
        </Button>
      ) : null}
      {permissions?.AllowDelete === true ? (
        <DeleteButton selectedId={selectedId} onClick={() => page.deleteSelected()} />
      ) : null}
      <ExcelExportButton running={page.excelExportRunning} onClick={() => page.exportExcel()} />
    </>
  );

  return (
    <>
      <MessageDialog
        open={message.open}
        onClose={message.close}
        caption={message.caption}
        width="650px"
        text={
          rechnungId > 0 ? (
            <ReceiptAssignmentDialog viewId={viewId} rechnungId={rechnungId} />
          ) : (
            message.text
          )
        }
      />
      <ReceiptDocumentUploadDialog
        open={uploadDialog.visible}
        onClose={() => {
          uploadDialog.hide();
        }}
        viewId={viewId}
        receiptId={selectedId ?? 0}
      />
      <ReceiptDocumentListDialog
        open={listDialog.visible}
        onClose={() => {
          listDialog.hide();
        }}
        viewId={viewId}
        receiptId={selectedId ?? 0}
      />

      <ViewPageFrame view={view} gridClassName="view-grid-Receipt" toolbar={toolbar}>
        <DataGrid<Receipt>
          ref={page.gridRef}
          id={`grid-rechnung-${view?.ViewName ?? ''}`}
          columns={page.columns}
          view={view}
          dataSource={page.dataSource}
          onCellSave={(item, columnName) => page.saveCell(item, columnName)}
          onSelectedIdChange={(id) => {
            page.setSelectedId(id);
          }}
        />
      </ViewPageFrame>

      {permissions?.AllowAudit === true ? (
        <ViewAuditSidebar
          entity={EntityTypes.Rechnung}
          viewId={viewId}
          selectedId={selectedId}
          open={auditSidebar.visible}
          onClose={() => {
            auditSidebar.hide();
          }}
        />
      ) : null}
      {permissions?.AllowCreate === true ? (
        <ReceiptSidebar
          open={receiptSidebar.visible}
          position="Right"
          target=".view-page"
          onHide={() => {
            receiptSidebar.hide();
          }}
        />
      ) : null}
    </>
  );
}

export default ReceiptView;
